import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Badge, Button, Spinner } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client/react';
import moment from 'moment';
import { Notify } from 'notiflix';

import { GET_EVENT_BY_GUID, GET_MY_EVENT_REGISTRATIONS, GET_USER_PAID_EVENTS } from 'helper/graphql/queries';
import { REGISTER_FOR_EVENT } from 'helper/graphql/mutations';
import { PATH_EVENTS } from 'routes/Paths';
import useAuth from 'hooks/useAuth';
import EventRegisterModal from './EventRegisterModal';
import EventPaymentModal from './EventPaymentModal';

// Parse speakersData JSON string → array of {name, title}
const parseSpeakers = (raw) => {
	if (!raw) return null;
	try {
		const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
		return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
	} catch { return null; }
};

const formatPrice = (price) => {
	if (!price || price === 0) return null;
	return `Ksh ${price.toLocaleString()}`;
};

// Palette matching EventCard
const TYPE_PALETTE = [
	{ color: '#1d9eb0', bg: 'rgba(29,158,176,0.1)', icon: 'fe-radio' },
	{ color: '#F68521', bg: 'rgba(246,133,33,0.1)', icon: 'fe-activity' },
	{ color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', icon: 'fe-star' },
	{ color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: 'fe-zap' },
	{ color: '#ef4444', bg: 'rgba(239,68,68,0.1)', icon: 'fe-heart' },
	{ color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: 'fe-award' },
];

const getTypeMeta = (eventType) => {
	if (!eventType) return TYPE_PALETTE[0];
	let hash = 0;
	for (let i = 0; i < eventType.length; i++) hash = eventType.charCodeAt(i) + ((hash << 5) - hash);
	return TYPE_PALETTE[Math.abs(hash) % TYPE_PALETTE.length];
};

const formatTypeLabel = (eventType) => {
	if (!eventType) return 'Event';
	return eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const EventDetailPage = () => {
	const { guid } = useParams();
	const { isAuthenticated } = useAuth();

	const [showRegisterModal, setShowRegisterModal] = useState(false);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [registeringGuid, setRegisteringGuid] = useState(null);

	const { data, loading } = useQuery(GET_EVENT_BY_GUID, {
		variables: { guid },
		fetchPolicy: 'cache-and-network',
		skip: !guid,
	});

	const { data: registrationsData, refetch: refetchRegistrations } = useQuery(GET_MY_EVENT_REGISTRATIONS, {
		skip: !isAuthenticated,
		fetchPolicy: 'cache-and-network',
	});

	// Check if user has already paid for this event
	const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
	const { data: paidEventsData, refetch: refetchPaidEvents } = useQuery(GET_USER_PAID_EVENTS, {
		variables: { userIdGuid: currentUser?.guid, eventGuid: guid },
		skip: !isAuthenticated || !currentUser?.guid,
		fetchPolicy: 'cache-and-network',
	});

	const [registerForEvent] = useMutation(REGISTER_FOR_EVENT);

	const event = useMemo(
		() => data?.allEvents?.edges?.[0]?.node || null,
		[data]
	);

	const registeredGuids = useMemo(() => {
		const regs = registrationsData?.myEventRegistrations || [];
		return new Set(regs.map((r) => r.event?.guid));
	}, [registrationsData]);

	const hasPaid = useMemo(() => {
		const payments = paidEventsData?.allPackagePayments?.edges || [];
		return payments.length > 0;
	}, [paidEventsData]);

	const handleConfirmRegister = async () => {
		if (!event) return;
		setRegisteringGuid(event.guid);
		try {
			await registerForEvent({ variables: { eventGuid: event.guid } });
			setShowRegisterModal(false);
			Notify.success('Registered! Check your email for the join link.');
			setTimeout(() => window.location.reload(), 1500);
		} catch (err) {
			const msg = err?.message || '';
			Notify.failure(msg || 'Registration failed. Please try again.');
			setRegisteringGuid(null);
			if (msg.toLowerCase().includes('already')) {
				setTimeout(() => window.location.reload(), 1500);
			}
		}
	};

	const handleModalHide = () => {
		const hasToken = !!localStorage.getItem('accessToken');
		setShowRegisterModal(false);
		if (hasToken && !isAuthenticated) {
			setTimeout(() => window.location.reload(), 100);
		}
	};

	// Loading state
	if (loading && !event) {
		return (
			<div className="edp-loading">
				<Spinner animation="border" style={{ color: '#F68521' }} />
				<p className="mt-3 text-muted">Loading event details…</p>
			</div>
		);
	}

	// Not found
	if (!loading && !event) {
		return (
			<div className="edp-not-found">
				<i className="fe fe-calendar" style={{ fontSize: '3rem', opacity: 0.2 }}></i>
				<h4 className="mt-3">Event not found</h4>
				<p className="text-muted">This event may have been removed or the link is incorrect.</p>
				<Link to={PATH_EVENTS.root} className="edp-back-btn">
					<i className="fe fe-arrow-left me-2"></i>Browse Events
				</Link>
			</div>
		);
	}

	const meta = getTypeMeta(event.eventType);
	const sessionDate = moment(event.sessionDatetime);
	const isUpcoming = sessionDate.isAfter(moment());
	const isPast = !isUpcoming;
	const isFull = event.maxParticipants > 0 && event.registrationCount >= event.maxParticipants;
	const spotsLeft = event.maxParticipants > 0 ? event.maxParticipants - event.registrationCount : null;
	const isRegistered = event.isRegistered || registeredGuids.has(event.guid);

	const speakers = parseSpeakers(event.speakersData) ||
		(event.speakerName ? [{ name: event.speakerName, title: event.speakerTitle }] : []);
	const virtualPriceLabel = formatPrice(event.price);
	const physicalPriceLabel = formatPrice(event.physicalPrice);
	const hasPricing = virtualPriceLabel || physicalPriceLabel;
	const isPaidEvent = (event.price > 0) || (event.physicalPrice > 0);

	const hasFeedbackForm = !!event.feedbackFormSchema;
	const shareUrl = window.location.href;

	const handleShare = () => {
		if (navigator.share) {
			navigator.share({ title: event.title, url: shareUrl });
		} else {
			navigator.clipboard.writeText(shareUrl).then(() =>
				Notify.success('Event link copied to clipboard!')
			);
		}
	};

	return (
		<>
			{/* Hero */}
			<div className="edp-hero" style={{ borderBottom: `4px solid ${meta.color}` }}>
				<Container>
					<div className="edp-hero-inner py-5">
						{/* Breadcrumb */}
						<div className="edp-breadcrumb mb-4 mt-6">
							<Link to={PATH_EVENTS.root} className="edp-breadcrumb-link">
								<i className="fe fe-arrow-left me-2"></i>All Events
							</Link>
						</div>

						<Row className="align-items-start g-5">
							{/* Poster column — full width on mobile (stacks above content), side column on md+ */}
							{event.bannerImage && (
								<Col xs={12} md={3} lg={2}>
									<div className="edp-poster-wrap edp-poster-mobile-center" style={{ borderColor: meta.color }}>
										<img src={event.bannerImage} alt={event.title} className="edp-poster-img" />
									</div>
								</Col>
							)}

							<Col xs={12} md={event.bannerImage ? 5 : 8} lg={event.bannerImage ? 6 : 8}>
								{/* Type + status badges */}
								<div className="d-flex align-items-center flex-wrap gap-2 mb-3">
									<Badge
										bg=""
										className="edp-type-badge"
										style={{ background: meta.bg, color: meta.color }}
									>
										<i className={`fe ${meta.icon} me-1`}></i>
										{formatTypeLabel(event.eventType)}
									</Badge>
									{isUpcoming ? (
										<Badge bg="" className="edp-status-badge edp-upcoming">Upcoming</Badge>
									) : (
										<Badge bg="" className="edp-status-badge edp-past">Past Event</Badge>
									)}
									{!event.isActive && (
										<Badge bg="" className="edp-status-badge edp-draft">Draft</Badge>
									)}
								</div>

								<h1 className="edp-title">{event.title}</h1>

								{event.description && (
									<p className="edp-description">{event.description}</p>
								)}

								{/* Speakers */}
								{speakers.length > 0 && (
									<div className="edp-speakers-wrap">
										{speakers.map((s, i) => (
											<div key={i} className="edp-speaker-block">
												<div className="edp-speaker-avatar">
													{s.name.charAt(0).toUpperCase()}
												</div>
												<div>
													<div className="edp-speaker-name">{s.name}</div>
													{s.title && (
														<div className="edp-speaker-title">{s.title}</div>
													)}
												</div>
											</div>
										))}
									</div>
								)}
							</Col>

							{/* Right panel: event meta + CTA */}
							<Col lg={4}>
								<div className="edp-info-card">
									{/* Date/time */}
									<div className="edp-info-row">
										<div className="edp-info-icon" style={{ background: `${meta.color}18`, color: meta.color }}>
											<i className="fe fe-calendar"></i>
										</div>
										<div>
											<div className="edp-info-label">Date</div>
											<div className="edp-info-value">{sessionDate.format('ddd, MMMM D, YYYY')}</div>
										</div>
									</div>
									<div className="edp-info-row">
										<div className="edp-info-icon" style={{ background: `${meta.color}18`, color: meta.color }}>
											<i className="fe fe-clock"></i>
										</div>
										<div>
											<div className="edp-info-label">Time</div>
											<div className="edp-info-value">
												{sessionDate.format('h:mm A')}
												{event.durationMinutes > 0 && (
													<span className="text-muted ms-1 fw-normal" style={{ fontSize: '0.8125rem' }}>
														({event.durationMinutes} min)
													</span>
												)}
											</div>
										</div>
									</div>
									{/* Registrations */}
									<div className="edp-info-row">
										<div className="edp-info-icon" style={{ background: `${meta.color}18`, color: meta.color }}>
											<i className="fe fe-users"></i>
										</div>
										<div>
											<div className="edp-info-label">Registrations</div>
											<div className="edp-info-value">
												{event.registrationCount}
												{event.maxParticipants > 0 && (
													<span className="text-muted ms-1 fw-normal" style={{ fontSize: '0.8125rem' }}>
														/ {event.maxParticipants}
													</span>
												)}
												{spotsLeft !== null && isUpcoming && !isFull && (
													<span
														className={`ms-2 fw-semibold`}
														style={{ fontSize: '0.8125rem', color: spotsLeft <= 5 ? '#ef4444' : '#10b981' }}
													>
														{spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left
													</span>
												)}
											</div>
										</div>
									</div>
									{/* Price */}
									{hasPricing && (
										<div className="edp-info-row">
											<div className="edp-info-icon" style={{ background: `${meta.color}18`, color: meta.color }}>
												<i className="fe fe-tag"></i>
											</div>
											<div>
												<div className="edp-info-label">Entry Fee</div>
												<div className="edp-info-value edp-price-wrap">
													{virtualPriceLabel && (
														<span className="edp-price-item edp-price-virtual">
															<i className="fe fe-monitor me-1"></i>
															{virtualPriceLabel}
															<span className="edp-price-mode">Online</span>
														</span>
													)}
													{physicalPriceLabel && (
														<span className="edp-price-item edp-price-physical">
															<i className="fe fe-map-pin me-1"></i>
															{physicalPriceLabel}
															<span className="edp-price-mode">In-Person</span>
														</span>
													)}
												</div>
											</div>
										</div>
									)}
									{/* Certificate note */}
									{hasFeedbackForm && (
										<div className="edp-cert-note">
											<i className="fe fe-award me-2" style={{ color: meta.color }}></i>
											E-certificate issued upon completing post-session feedback
										</div>
									)}

									{/* CTA */}
									<div className="mt-4">
										{isRegistered ? (
											<div className="edp-registered-badge">
												<i className="fe fe-check-circle me-2"></i>
												You're registered
												{hasPaid && (
													<span className="ms-2 edp-paid-chip">
														<i className="fe fe-credit-card me-1"></i>Paid
													</span>
												)}
											</div>
										) : isUpcoming && !isFull ? (
											<Button
												className="edp-register-btn w-100"
												onClick={() => isPaidEvent ? setShowPaymentModal(true) : setShowRegisterModal(true)}
												disabled={!!registeringGuid}
											>
												<i className={`fe ${isPaidEvent ? 'fe-credit-card' : 'fe-user-plus'} me-2`}></i>
												{isPaidEvent
													? `Register & Pay`
													: 'Register for this Event'}
											</Button>
										) : isFull ? (
											<Button className="edp-register-btn w-100" disabled>
												<i className="fe fe-slash me-2"></i>Fully Booked
											</Button>
										) : (
											<Button className="edp-register-btn w-100" disabled>
												<i className="fe fe-calendar me-2"></i>Session Ended
											</Button>
										)}

										{/* Post-event feedback CTA */}
										{isPast && isRegistered && hasFeedbackForm && (
											<Link
												to={PATH_EVENTS.feedback(guid)}
												className="edp-feedback-btn w-100 d-flex align-items-center justify-content-center mt-2"
											>
												<i className="fe fe-edit-3 me-2"></i>Submit Feedback &amp; Get Certificate
											</Link>
										)}

										{/* Share button */}
										<Button
											variant="light"
											className="edp-share-btn w-100 mt-2"
											onClick={handleShare}
										>
											<i className="fe fe-share-2 me-2"></i>Share this Event
										</Button>
									</div>
								</div>
							</Col>
						</Row>
					</div>
				</Container>
			</div>

			{/* Free registration modal */}
			<EventRegisterModal
				show={showRegisterModal}
				onHide={handleModalHide}
				event={event}
				onConfirm={handleConfirmRegister}
				loading={!!registeringGuid}
			/>

			{/* Paid registration modal */}
			<EventPaymentModal
				show={showPaymentModal}
				onHide={() => setShowPaymentModal(false)}
				event={event}
				onSuccess={() => {
					setShowPaymentModal(false);
					refetchRegistrations();
					refetchPaidEvents();
					setTimeout(() => window.location.reload(), 1500);
				}}
			/>

			<style>{`
				/* Loading / not found */
				.edp-loading, .edp-not-found {
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					min-height: 60vh;
					color: #94a3b8;
					text-align: center;
					padding: 2rem;
				}
				.edp-back-btn {
					display: inline-flex;
					align-items: center;
					margin-top: 1rem;
					padding: 0.5rem 1.25rem;
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white;
					border-radius: 10px;
					font-weight: 600;
					font-size: 0.875rem;
					text-decoration: none;
				}
				.edp-back-btn:hover { opacity: 0.9; color: white; }

				/* Hero */
				.edp-hero {
					background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
					color: white;
					min-height: calc(100vh - 64px);
					display: flex;
					align-items: center;
					flex: 1;
				}
				.edp-hero-inner { width: 100%; }

				/* Poster */
				.edp-poster-wrap {
					border-radius: 12px;
					overflow: hidden;
					border: 3px solid;
					box-shadow: 0 12px 32px rgba(0,0,0,0.4);
					aspect-ratio: 2/3;
				}
				.edp-poster-img {
					width: 100%;
					height: 100%;
					object-fit: cover;
					display: block;
				}

				/* Breadcrumb */
				.edp-breadcrumb-link {
					font-size: 0.875rem;
					font-weight: 600;
					color: rgba(255,255,255,0.6);
					text-decoration: none;
					display: inline-flex;
					align-items: center;
					transition: color 0.2s;
				}
				.edp-breadcrumb-link:hover { color: white; }

				/* Badges */
				.edp-type-badge {
					font-size: 0.75rem;
					font-weight: 700;
					padding: 5px 12px;
					border-radius: 8px;
					display: inline-flex;
					align-items: center;
				}
				.edp-status-badge {
					font-size: 0.6875rem;
					font-weight: 700;
					padding: 4px 10px;
					border-radius: 6px;
				}
				.edp-upcoming { background: rgba(16,185,129,0.2) !important; color: #34d399 !important; }
				.edp-past { background: rgba(148,163,184,0.2) !important; color: #94a3b8 !important; }
				.edp-draft { background: rgba(245,158,11,0.2) !important; color: #fbbf24 !important; }

				/* Title & description */
				.edp-title {
					font-size: 2rem;
					font-weight: 800;
					color: white;
					line-height: 1.25;
					margin-bottom: 1rem;
				}
				.edp-description {
					font-size: 1rem;
					color: rgba(255,255,255,0.7);
					line-height: 1.75;
					margin-bottom: 1.5rem;
					max-width: 600px;
				}

				/* Speakers */
				.edp-speakers-wrap {
					display: flex;
					flex-wrap: wrap;
					gap: 0.625rem;
				}
				.edp-speaker-block {
					display: flex;
					align-items: center;
					gap: 0.875rem;
					background: rgba(255,255,255,0.07);
					border: 1px solid rgba(255,255,255,0.1);
					border-radius: 12px;
					padding: 0.875rem 1.125rem;
					width: fit-content;
				}
				.edp-speaker-avatar {
					width: 44px;
					height: 44px;
					border-radius: 50%;
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					display: flex;
					align-items: center;
					justify-content: center;
					font-weight: 800;
					font-size: 1.125rem;
					color: white;
					flex-shrink: 0;
				}
				.edp-speaker-name { font-size: 0.9375rem; font-weight: 700; color: white; }
				.edp-speaker-title { font-size: 0.8125rem; color: rgba(255,255,255,0.6); }

				/* Info card */
				.edp-info-card {
					background: white;
					border-radius: 20px;
					padding: 1.75rem;
					box-shadow: 0 20px 60px rgba(0,0,0,0.25);
				}
				.edp-info-row {
					display: flex;
					align-items: flex-start;
					gap: 1rem;
					padding: 0.875rem 0;
					border-bottom: 1px solid #f1f5f9;
				}
				.edp-info-row:first-child { padding-top: 0; }
				.edp-info-row:last-of-type { border-bottom: none; padding-bottom: 0; }
				.edp-info-icon {
					width: 40px;
					height: 40px;
					border-radius: 10px;
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 1.125rem;
					flex-shrink: 0;
				}
				.edp-info-label {
					font-size: 0.75rem;
					font-weight: 600;
					color: #94a3b8;
					text-transform: uppercase;
					letter-spacing: 0.4px;
					margin-bottom: 0.2rem;
				}
				.edp-info-value {
					font-size: 0.9375rem;
					font-weight: 700;
					color: #1e293b;
					display: flex;
					align-items: center;
					flex-wrap: wrap;
				}

				/* Dual pricing */
				.edp-price-wrap {
					display: flex;
					flex-direction: column;
					gap: 0.375rem;
					align-items: flex-start;
				}
				.edp-price-item {
					display: inline-flex;
					align-items: center;
					gap: 0.25rem;
					font-size: 0.9rem;
					font-weight: 700;
					border-radius: 6px;
					padding: 2px 8px 2px 6px;
				}
				.edp-price-virtual {
					background: rgba(16,185,129,0.08);
					color: #10b981;
				}
				.edp-price-physical {
					background: rgba(246,133,33,0.08);
					color: #F68521;
				}
				.edp-price-mode {
					font-size: 0.6875rem;
					font-weight: 600;
					margin-left: 4px;
					opacity: 0.7;
					text-transform: uppercase;
					letter-spacing: 0.3px;
				}

				/* Certificate note */
				.edp-cert-note {
					margin-top: 1rem;
					padding: 0.75rem 1rem;
					background: #f8fafc;
					border-radius: 10px;
					font-size: 0.8125rem;
					color: #475569;
					display: flex;
					align-items: center;
					border: 1px solid #e2e8f0;
				}

				/* Buttons */
				.edp-register-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					border: none;
					color: white;
					font-weight: 600;
					font-size: 0.9375rem;
					padding: 0.75rem 1rem;
					border-radius: 12px;
					display: flex;
					align-items: center;
					justify-content: center;
					transition: opacity 0.2s;
				}
				.edp-register-btn:hover:not(:disabled) { opacity: 0.9; color: white; background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%); }
				.edp-register-btn:disabled { opacity: 0.6; background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%); color: white; }
				.edp-registered-badge {
					display: flex;
					align-items: center;
					justify-content: center;
					background: rgba(16,185,129,0.1);
					color: #10b981;
					font-weight: 700;
					font-size: 0.875rem;
					padding: 0.75rem 1rem;
					border-radius: 12px;
					border: 1.5px solid rgba(16,185,129,0.3);
					flex-wrap: wrap;
					gap: 0.25rem;
				}
				.edp-paid-chip {
					background: rgba(16,185,129,0.15);
					border-radius: 6px;
					padding: 2px 8px;
					font-size: 0.75rem;
					display: inline-flex;
					align-items: center;
				}
				.edp-feedback-btn {
					background: linear-gradient(135deg, #1d9eb0 0%, #0e7490 100%);
					border: none;
					color: white;
					font-weight: 600;
					font-size: 0.875rem;
					padding: 0.625rem 1rem;
					border-radius: 10px;
					text-decoration: none;
					transition: opacity 0.2s;
				}
				.edp-feedback-btn:hover { opacity: 0.9; color: white; }
				.edp-share-btn {
					border: 1.5px solid #e2e8f0 !important;
					color: #64748b !important;
					font-weight: 600;
					font-size: 0.875rem;
					border-radius: 10px;
					background: white !important;
					display: flex;
					align-items: center;
					justify-content: center;
					transition: all 0.2s;
				}
				.edp-share-btn:hover { border-color: #94a3b8 !important; color: #1e293b !important; }

				/* Responsive */
				@media (max-width: 992px) {
					.edp-title { font-size: 1.5rem; }
					.edp-info-card { margin-top: 1.5rem; }
				}
				@media (max-width: 767px) {
					.edp-poster-mobile-center {
						max-width: 180px;
						margin: 0 auto;
					}
				}
			`}</style>
		</>
	);
};

export default EventDetailPage;
